import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useBranch } from '../contexts/BranchContext';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useBrandBranches } from '../hooks/useBrandQueries';
import { useDeskAdmin } from '../hooks/useDeskAdmin';
import { useMobileShell } from '../hooks/useMobileShell';
import { useMobileShellContext } from '../contexts/MobileShellContext';
import { useNavigationMenu } from '../hooks/useNavigationMenu';
import { searchMenu, type MenuSearchResult } from '../utils/menuSearch';
import { APP_VERSION_BADGE } from '../data/appVersion';
import MenuSearchInput, { type MenuSearchInputHandle } from './sidebar/MenuSearchInput';
import MenuSearchResults from './sidebar/MenuSearchResults';
import { getMenuIcon } from './sidebar/menuIcons';
import { isDemoHost } from '../config/api.config';

const SEARCH_LISTBOX_ID = 'sidebar-search-listbox';

const Sidebar: React.FC = () => {
  const { selectedBranch } = useBranch();
  const [collapsed, setCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeResultIndex, setActiveResultIndex] = useState(-1);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { data: branches = [] } = useBrandBranches(selectedBranch?.brandId || null);
  const { isAdmin } = useDeskAdmin();
  const showBrandDashboards = branches.length > 1;
  const isMobileShell = useMobileShell();
  const { mobileMenuOpen, closeMobileMenu } = useMobileShellContext();
  const searchInputRef = useRef<MenuSearchInputHandle>(null);
  const sidebarContentRef = useRef<HTMLDivElement>(null);
  const savedScrollTopRef = useRef(0);

  const { visibleSections, visibleDashboardIds } = useNavigationMenu({
    showBrandDashboards,
    isAdmin,
    isDemo: isDemoHost(),
  });

  const selectedDashboard = location.pathname.split('/').pop() || 'main';
  const trimmedQuery = searchQuery.trim();
  const isSearching = trimmedQuery.length > 0;

  const searchResults = useMemo(
    () => searchMenu(trimmedQuery, visibleSections, visibleDashboardIds),
    [trimmedQuery, visibleSections, visibleDashboardIds],
  );

  const activeDescendantId =
    isSearching && activeResultIndex >= 0 && activeResultIndex < searchResults.length
      ? `${SEARCH_LISTBOX_ID}-option-${activeResultIndex}`
      : undefined;

  const toggleSidebar = () => {
    if (isMobileShell) return;
    setCollapsed(!collapsed);
  };

  const handleExpandAndFocusSearch = useCallback(() => {
    if (isMobileShell) return;
    setCollapsed(false);
    window.requestAnimationFrame(() => {
      window.setTimeout(() => searchInputRef.current?.focus(), 320);
    });
  }, [isMobileShell]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setActiveResultIndex(-1);
  }, []);

  const navigateToResult = useCallback(
    (result: MenuSearchResult) => {
      const currentParams = searchParams.toString();
      const basePath = `/dashboard/${result.dashboardId}${currentParams ? `?${currentParams}` : ''}`;
      const targetPath =
        result.type === 'section' && result.sectionId
          ? `${basePath}#section-${result.sectionId}`
          : basePath;

      navigate(targetPath);
      clearSearch();
      if (isMobileShell) closeMobileMenu();
    },
    [searchParams, navigate, clearSearch, isMobileShell, closeMobileMenu],
  );

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      clearSearch();
      return;
    }

    if (!isSearching || searchResults.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveResultIndex((prev) => (prev + 1) % searchResults.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveResultIndex((prev) => (prev <= 0 ? searchResults.length - 1 : prev - 1));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const index = activeResultIndex >= 0 ? activeResultIndex : 0;
      const result = searchResults[index];
      if (result) navigateToResult(result);
    }
  };

  useEffect(() => {
    if (!mobileMenuOpen) return;
    document.body.classList.add('overlay-scroll-lock');
    return () => document.body.classList.remove('overlay-scroll-lock');
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) {
      clearSearch();
    }
  }, [mobileMenuOpen, clearSearch]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && mobileMenuOpen) {
        closeMobileMenu();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileMenuOpen, closeMobileMenu]);

  useEffect(() => {
    if (isSearching) {
      if (sidebarContentRef.current) {
        savedScrollTopRef.current = sidebarContentRef.current.scrollTop;
      }
      setActiveResultIndex(searchResults.length > 0 ? 0 : -1);
    } else if (sidebarContentRef.current) {
      sidebarContentRef.current.scrollTop = savedScrollTopRef.current;
    }
  }, [isSearching, trimmedQuery, searchResults.length]);

  const showExpandedSearch = !collapsed || isMobileShell;

  return (
    <>
      {isMobileShell && mobileMenuOpen && (
        <button
          type="button"
          className="mobile-drawer-backdrop overlay-drawer fixed inset-0 bg-black/40 lg:hidden"
          aria-label="بستن منو"
          onClick={closeMobileMenu}
        />
      )}
      <div
        className={`app-sidebar ${collapsed && !isMobileShell ? 'collapsed' : ''} ${
          isMobileShell ? `mobile-drawer-panel ${mobileMenuOpen ? 'mobile-drawer-open' : ''}` : ''
        }`}
        role={isMobileShell && mobileMenuOpen ? 'dialog' : undefined}
        aria-modal={isMobileShell && mobileMenuOpen ? true : undefined}
        aria-label={isMobileShell ? 'منوی داشبوردها' : undefined}
      >
        <div className="sidebar-header">
          <button
            className="collapse-button"
            onClick={toggleSidebar}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            )}
          </button>
        </div>

        <MenuSearchInput
          ref={searchInputRef}
          value={searchQuery}
          onChange={setSearchQuery}
          onClear={clearSearch}
          onKeyDown={handleSearchKeyDown}
          collapsed={!showExpandedSearch}
          onCollapsedClick={handleExpandAndFocusSearch}
          listboxId={SEARCH_LISTBOX_ID}
          isExpanded={isSearching}
          activeDescendantId={activeDescendantId}
        />

        <div className="sidebar-content" ref={sidebarContentRef}>
          {isSearching ? (
            <MenuSearchResults
              results={searchResults}
              listboxId={SEARCH_LISTBOX_ID}
              activeIndex={activeResultIndex}
              onSelect={navigateToResult}
              onHighlight={setActiveResultIndex}
            />
          ) : (
            <div className="sidebar-menu">
              {visibleSections.map((section, sectionIndex) => (
                <div key={section.title} className="menu-section">
                  {!collapsed && <div className="section-title">{section.title}</div>}
                  {section.items.map((dashboard, itemIndex) => {
                    const currentParams = searchParams.toString();
                    const targetPath = `/dashboard/${dashboard.id}${currentParams ? `?${currentParams}` : ''}`;

                    return (
                      <div
                        key={dashboard.id}
                        className={`menu-item ${selectedDashboard === dashboard.id ? 'active' : ''}`}
                        onClick={() => {
                          navigate(targetPath);
                          if (isMobileShell) closeMobileMenu();
                        }}
                        data-tooltip={dashboard.name}
                        style={{ '--item-index': itemIndex } as React.CSSProperties}
                      >
                        <div className="item-icon">{getMenuIcon(dashboard.icon)}</div>
                        {!collapsed && (
                          <span className="item-text flex items-start gap-1.5 min-w-0">
                            <span className="min-w-0 flex-1 whitespace-normal break-words">{dashboard.name}</span>
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {sectionIndex < visibleSections.length - 1 && <div className="section-divider"></div>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="sidebar-footer">
          <div
            className="app-version"
            role="button"
            tabIndex={0}
            title={`مشاهده تاریخچه تغییرات ${APP_VERSION_BADGE}`}
            onClick={() => navigate(`/dashboard/changelog${location.search}`)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(`/dashboard/changelog${location.search}`);
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            {!collapsed && <span>{`نسخه ${APP_VERSION_BADGE}`}</span>}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
